import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import styled from 'styled-components';
import { useTheme } from '../contexts/ThemeContext';
import { Card } from '../design-system/components/Card';
import { Button } from '../design-system/components/Button';
import { Heading, Text } from '../design-system/components/Typography';
import { Stack, Container } from '../design-system/components/Layout';
import { Icon } from '../components/common/Icon';
import { iconMappings } from '../utils/iconMappings';
import { spacing } from '../design-system/tokens/spacing';
import { typography } from '../design-system/tokens/typography';
import { hexToRgba } from '../utils/colorUtils';
import { authService } from '../services/auth';
import { useToast } from '../hooks/useToast';

const SuccessContainer = styled(Container)`
  max-width: 600px;
  padding: ${spacing[10]} ${spacing[5]};
  background: ${props => props.theme.colors.gradientBackground};
`;

const SuccessCard = styled(Card)`
  padding: ${spacing[8]};
  text-align: center;
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${props => hexToRgba(props.theme.colors.success, 0.1)};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${spacing[6]};
`;

const EmailHighlight = styled.div`
  background: ${props => props.theme.colors.surfaceHover};
  padding: ${spacing[4]};
  border-radius: ${props => props.theme.borders?.radius?.md || '8px'};
  margin: ${spacing[4]} 0;
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.primary};
`;

const ResendButton = styled(Button)`
  margin-top: ${spacing[4]};
`;

const HeadingWithMargin = styled(Heading)`
  margin-bottom: ${spacing[4]};
`;

const TextWithMargin = styled(Text)`
  margin-bottom: ${spacing[6]};
`;

const TextWithSmallMargin = styled(Text)`
  margin-bottom: ${spacing[2]};
`;

const TextWithMediumMargin = styled(Text)`
  margin-bottom: ${spacing[4]};
`;

const FullWidthDiv = styled.div`
  width: 100%;
`;

const DividerDiv = styled.div`
  margin-top: ${spacing[6]};
  padding-top: ${spacing[6]};
  border-top: 1px solid ${props => props.theme.colors.border};
  width: 100%;
`;

const StyledLink = styled(Link)`
  color: ${props => props.theme.colors.primary};
  font-weight: ${typography.fontWeight.semibold};
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const RegisterSuccess: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { success: showSuccess, error: showError } = useToast();

  const email = (location.state as any)?.email || '';
  const message = (location.state as any)?.message || t('register.checkEmailConfirmation');
  const applicationReferenceNumber = (location.state as any)?.applicationReferenceNumber as string | undefined;

  const [resending, setResending] = React.useState(false);

  const handleResendConfirmation = async () => {
    if (!email) {
      showError(t('register.emailNotFound'));
      return;
    }

    setResending(true);
    try {
      await authService.resendConfirmationEmail(email);
      showSuccess(t('register.confirmationEmailResent'));
    } catch (error: any) {
      showError(error.message || t('register.resendFailed'));
    } finally {
      setResending(false);
    }
  };

  return (
    <SuccessContainer>
      <SuccessCard variant="elevated" headerVariant="gradient" accentBorder="top" pattern="dots" patternIntensity="subtle">
        <Stack direction="column" spacing="lg" align="center">
          <IconWrapper>
            <Icon icon={iconMappings.envelope} size="xl" color="success" />
          </IconWrapper>

          <div>
            <HeadingWithMargin variant="h2" color="text">
              {t('register.registrationSuccessful')}
            </HeadingWithMargin>
            <TextWithMargin size="base" color="secondary">
              {message}
            </TextWithMargin>
          </div>

          {applicationReferenceNumber && (
            <FullWidthDiv>
              <TextWithSmallMargin size="sm" color="secondary">
                {t('register.applicationReferenceNumber')}
              </TextWithSmallMargin>
              <EmailHighlight>{applicationReferenceNumber}</EmailHighlight>
              <TextWithSmallMargin size="sm" color="secondary">
                {t('register.checkApplicationStatusHint')}{' '}
                <StyledLink to="/application-status">{t('register.checkStatus')}</StyledLink>
              </TextWithSmallMargin>
            </FullWidthDiv>
          )}

          {email && (
            <FullWidthDiv>
              <TextWithSmallMargin size="sm" color="secondary">
                {t('register.confirmationEmailSentTo')}
              </TextWithSmallMargin>
              <EmailHighlight>{email}</EmailHighlight>
            </FullWidthDiv>
          )}

          <FullWidthDiv>
            <TextWithMediumMargin size="sm" color="secondary">
              {t('register.didNotReceiveEmail')}
            </TextWithMediumMargin>
            <ResendButton
              variant="outline"
              onClick={handleResendConfirmation}
              loading={resending}
              disabled={!email}
            >
              {t('register.resendConfirmation')}
            </ResendButton>
          </FullWidthDiv>

          <DividerDiv>
            <Text size="sm" color="secondary">
              {t('register.alreadyConfirmed')}
              <StyledLink to="/login">
                {t('login.submit')}
              </StyledLink>
            </Text>
          </DividerDiv>
        </Stack>
      </SuccessCard>
    </SuccessContainer>
  );
};

export default RegisterSuccess;
